import { Alert, Form } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CandidatoService } from '../../candidato.service';
import { EMAIL_INSTITUCIONAL_DOMINIO } from '../../candidato.constants';
import { TipoLotacaoEstagioList } from '@/shared/constants/enumlist';
import { WizardStepActions } from '@/shared/components/forms/WizardStepActions';
import { readPerson, updatePerson } from '@/modules/people/person.service';
import { DataIso } from '@/shared/utils/datas';
import type { PromocaoDadosSalvos } from './promover-estagiario-wizard.models';

const candidatoService = new CandidatoService();

export type PromoverPreview = {
  candidatoId: number;
  pessoaId: string;
  nome: string;
  cpf?: string;
  emailPessoal?: string;
  cursoId?: number;
  instituicaoEnsinoId?: number;
  instituicaoEnsinoNome?: string;
  comarcaPretendidaId?: number;
  data_inicio_estagio_sugerida?: string;
};

type Props = {
  candidatoId: number;
  promocaoConcluida?: boolean;
  promocaoDados?: PromocaoDadosSalvos;
  pessoaId?: string;
  onSuccess: (data: { pessoaId: string; nome: string; promocaoDados: PromocaoDadosSalvos }) => void;
  onCancel: () => void;
};

function formatDateInput(value?: string | Date | null) {
  return value ? DataIso(value, 'yyyy-MM-dd') : '';
}

export function PromoverEstagiarioStepForm({
  candidatoId,
  promocaoConcluida = false,
  promocaoDados,
  pessoaId,
  onSuccess,
  onCancel,
}: Props) {
  const [preview, setPreview] = useState<PromoverPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tipoEstagio, setTipoEstagio] = useState(
    promocaoDados?.tipoEstagio ?? String(TipoLotacaoEstagioList[0].value),
  );
  const [emailInstitucional, setEmailInstitucional] = useState(
    promocaoDados?.email ?? EMAIL_INSTITUCIONAL_DOMINIO,
  );
  const [dataInicio, setDataInicio] = useState(promocaoDados?.dataInicio ?? '');
  const [dataFim, setDataFim] = useState(promocaoDados?.dataFim ?? '');
  const [fimSemana, setFimSemana] = useState(promocaoDados?.fimSemana ?? false);

  useEffect(() => {
    let cancelled = false;

    const carregarPromocaoExistente = async () => {
      if (!pessoaId) {
        setLoading(false);
        return;
      }

      try {
        const res = await readPerson({
          skip: 0,
          take: 1,
          id: pessoaId,
          comEstagiario: true,
          comEstagios: true,
        });
        if (cancelled) return;

        const pessoa = res[0]?.[0];
        const estagios = pessoa?.estagiario?.estagios ?? [];
        const estagioAtivo = estagios.find((e) => !e.desligamento) ?? estagios[0];

        setPreview({
          candidatoId,
          pessoaId,
          nome: pessoa?.nome ?? promocaoDados?.nome ?? '',
          cpf: pessoa?.cpf,
        });

        if (promocaoDados) {
          setTipoEstagio(promocaoDados.tipoEstagio);
          setEmailInstitucional(promocaoDados.email);
          setDataInicio(promocaoDados.dataInicio);
          setDataFim(promocaoDados.dataFim);
          setFimSemana(promocaoDados.fimSemana);
        } else if (estagioAtivo) {
          setTipoEstagio(String(estagioAtivo.tipo_estagio ?? TipoLotacaoEstagioList[0].value));
          setEmailInstitucional(estagioAtivo.email ?? pessoa?.emailInstitucional ?? EMAIL_INSTITUCIONAL_DOMINIO);
          setDataInicio(formatDateInput(estagioAtivo.data_inicio_estagio));
          setDataFim(formatDateInput(estagioAtivo.data_fim_estagio));
          setFimSemana(Boolean(estagioAtivo.fim_semana));
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Falha ao carregar dados da promocao.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (promocaoConcluida) {
      void carregarPromocaoExistente();
      return () => {
        cancelled = true;
      };
    }

    if (candidatoId <= 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    candidatoService
      .getPromoverPreview(candidatoId)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        if (!promocaoDados) {
          setDataInicio(p.data_inicio_estagio_sugerida ?? '');
          setEmailInstitucional(EMAIL_INSTITUCIONAL_DOMINIO);
        }
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.message || 'Falha ao carregar preview.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [candidatoId, promocaoConcluida, promocaoDados, pessoaId]);

  const buildPromocaoDados = (): PromocaoDadosSalvos => ({
    email: emailInstitucional,
    tipoEstagio,
    dataInicio,
    dataFim,
    fimSemana,
    nome: preview?.nome ?? promocaoDados?.nome ?? '',
  });

  const salvarPromocaoExistente = async () => {
    if (!pessoaId) return;

    const email = emailInstitucional.trim();
    const localPart = email.split('@')[0]?.trim();
    if (!localPart || !dataInicio) {
      toast.warn('Preencha o usuario do e-mail institucional e a data de inicio.');
      return;
    }

    setSaving(true);
    try {
      const res = await readPerson({
        skip: 0,
        take: 1,
        id: pessoaId,
        comEstagiario: true,
        comEstagios: true,
      });
      const pessoa = res[0]?.[0];
      const estagios = pessoa?.estagiario?.estagios ?? [];
      const estagioAtivo = estagios.find((e) => !e.desligamento) ?? estagios[0];

      if (!pessoa?.estagiario?.id || !estagioAtivo?.id) {
        toast.error('Nao foi possivel identificar o estagio para atualizacao.');
        return;
      }

      await updatePerson({
        id: pessoaId,
        nome: pessoa.nome,
        cpf: pessoa.cpf?.replace(/\D/g, '') ?? pessoa.cpf,
        emailInstitucional: email,
        estagiario: {
          id: pessoa.estagiario.id,
          ativo: true,
          estagios: [
            {
              id: estagioAtivo.id,
              tipo_estagio: Number(tipoEstagio) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
              email,
              data_inicio_estagio: DataIso(dataInicio),
              data_fim_estagio: dataFim ? DataIso(dataFim) : undefined,
              fim_semana: fimSemana,
              curso: estagioAtivo.curso?.id ? { id: estagioAtivo.curso.id } : undefined,
              instituicao_ensino: estagioAtivo.instituicao_ensino?.id
                ? { id: estagioAtivo.instituicao_ensino.id }
                : undefined,
            },
          ],
        },
      });

      toast.success('Dados da promocao atualizados.');
      onSuccess({
        pessoaId,
        nome: pessoa.nome ?? preview?.nome ?? '',
        promocaoDados: buildPromocaoDados(),
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Falha ao atualizar dados da promocao.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const executarPromocao = async () => {
    if (promocaoConcluida && pessoaId) {
      await salvarPromocaoExistente();
      return;
    }

    const email = emailInstitucional.trim();
    const localPart = email.split('@')[0]?.trim();
    if (!localPart || !dataInicio) {
      toast.warn('Preencha o usuario do e-mail institucional e a data de inicio.');
      return;
    }
    setSaving(true);
    try {
      const { pessoaId: novoPessoaId } = await candidatoService.promoverEstagiario(candidatoId, {
        tipo_estagio: Number(tipoEstagio),
        email,
        data_inicio_estagio: dataInicio,
        data_fim_estagio: dataFim || undefined,
        fim_semana: fimSemana,
      });
      toast.success('Candidato promovido a estagiario.');
      onSuccess({
        pessoaId: novoPessoaId,
        nome: preview?.nome ?? '',
        promocaoDados: buildPromocaoDados(),
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Falha ao promover candidato.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    void executarPromocao();
  };

  if (loading) {
    return <p className="text-muted mb-0">{'Carregando dados para promo\u00e7\u00e3o\u2026'}</p>;
  }

  if (!preview) {
    return (
      <p className="text-muted mb-0">
        {'N\u00e3o foi poss\u00edvel carregar os dados do candidato para promo\u00e7\u00e3o.'}
      </p>
    );
  }

  return (
    <Form onSubmit={(ev) => void handleSubmit(ev)}>
      {promocaoConcluida && (
        <Alert variant="info" className="py-2 small">
          <i className="bi bi-pencil-square me-1" />
          {'A promocao ja foi realizada. Voce pode alterar os dados abaixo e clicar em '}
          <strong>{'Salvar e continuar'}</strong>
          {' para seguir o cadastro.'}
        </Alert>
      )}

      <p className="small text-muted">
        {'Dados herdados da inscri\u00e7\u00e3o: '}
        <strong>{preview.nome}</strong>
        {preview.cpf ? ` \u2014 CPF ${preview.cpf}` : ''}
      </p>
      <div className="row g-3">
        <div className="col-md-6">
          <Form.Label>{'E-mail institucional do est\u00e1gio'}</Form.Label>
          <Form.Control
            type="email"
            value={emailInstitucional}
            onChange={(e) => setEmailInstitucional(e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <Form.Label>{'Tipo de est\u00e1gio'}</Form.Label>
          <Form.Select value={tipoEstagio} onChange={(e) => setTipoEstagio(e.target.value)}>
            {TipoLotacaoEstagioList.map((item) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-6">
          <Form.Label>{'Data in\u00edcio'}</Form.Label>
          <Form.Control
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <Form.Label>Data fim (opcional)</Form.Label>
          <Form.Control
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </div>
        <div className="col-12">
          <Form.Check
            type="checkbox"
            label={'Est\u00e1gio inclui fim de semana'}
            checked={fimSemana}
            onChange={(e) => setFimSemana(e.target.checked)}
          />
        </div>
      </div>

      <WizardStepActions
        showBack={false}
        secondaryLabel="Cancelar"
        onSecondary={onCancel}
        primaryLabel={promocaoConcluida ? 'Salvar e continuar' : 'Promover e continuar'}
        primaryLoadingLabel={promocaoConcluida ? 'Salvando...' : 'Promovendo...'}
        isLoading={saving}
      />
    </Form>
  );
}
